import React, { useState, useEffect, useCallback } from 'react';
import { FileText, X, Save, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import {
  initializeAssessment,
  saveAssessmentSection,
  getAssessment,
  completeAssessment,
  calculateCompletionPercentage
} from '../../services/beneficiaryAssessmentService';
import { applyProfileEnrichment } from '../../services/profileEnrichmentService';
import { useToastContext } from '../../contexts/ToastContext';
import AssessmentSection1_Prealables from './sections/AssessmentSection1_Prealables';
import AssessmentSection2_Identite from './sections/AssessmentSection2_Identite';
import AssessmentSection3_Contacts from './sections/AssessmentSection3_Contacts';
import AssessmentSection4_Logement from './sections/AssessmentSection4_Logement';
import AssessmentSection5_Sante from './sections/AssessmentSection5_Sante';
import AssessmentSection6_Autonomie from './sections/AssessmentSection6_Autonomie';
import {
  AssessmentSection7_Psychologie,
  AssessmentSection8_Aides,
  AssessmentSection9_Besoins,
  AssessmentSection10_Projet
} from './sections/AssessmentSections7to10';
import ProfileEnrichmentModal from './ProfileEnrichmentModal';
import './BeneficiaryAssessmentWizard.css';

/**
 * Wizard de questionnaire d'évaluation des bénéficiaires
 * Permet de remplir le questionnaire en plusieurs étapes avec sauvegarde automatique
 */
const BeneficiaryAssessmentWizard = ({
  db,
  beneficiaryId,
  beneficiary,
  user,
  onClose,
  onComplete
}) => {
  const { showToast } = useToastContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEnrichmentModal, setShowEnrichmentModal] = useState(false);

  const steps = [
    { number: 1, label: 'Préalables', section: 'prealables' },
    { number: 2, label: 'Identité', section: 'familialStatus' },
    { number: 3, label: 'Contacts', section: 'contacts' },
    { number: 4, label: 'Logement', section: 'housing' },
    { number: 5, label: 'Santé', section: 'health' },
    { number: 6, label: 'Autonomie', section: 'autonomy' },
    { number: 7, label: 'Psychologie', section: 'psychological' },
    { number: 8, label: 'Aides', section: 'currentAids' },
    { number: 9, label: 'Besoins', section: 'needs' },
    { number: 10, label: 'Projet', section: 'project' },
  ];

  // Debug: track showEnrichmentModal changes
  useEffect(() => {
    console.log('[Wizard] showEnrichmentModal a changé:', showEnrichmentModal);
  }, [showEnrichmentModal]);

  // Charger le questionnaire existant ou initialiser un nouveau
  useEffect(() => {
    const loadAssessment = async () => {
      try {
        setIsLoading(true);
        let assessment = await getAssessment(db, beneficiaryId);

        if (!assessment) {
          // Initialiser un nouveau questionnaire
          assessment = await initializeAssessment(db, beneficiaryId);
          showToast('Nouveau questionnaire initialisé', 'info');
        }

        setAssessmentData(assessment);
      } catch (error) {
        console.error('Erreur lors du chargement du questionnaire:', error);
        showToast('Erreur lors du chargement du questionnaire', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadAssessment();
  }, [db, beneficiaryId, showToast]);

  // Sauvegarde automatique toutes les 2 minutes
  useEffect(() => {
    if (!assessmentData) return;

    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, 120000); // 2 minutes

    return () => clearInterval(autoSaveInterval);
  }, [assessmentData]);

  // Sauvegarde automatique
  const handleAutoSave = useCallback(async () => {
    if (!assessmentData || isSaving) return;

    try {
      setIsSaving(true);
      const currentSection = steps[currentStep - 1].section;
      await saveAssessmentSection(db, beneficiaryId, currentSection, assessmentData[currentSection]);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Erreur lors de la sauvegarde automatique:', error);
    } finally {
      setIsSaving(false);
    }
  }, [assessmentData, currentStep, db, beneficiaryId, isSaving]);

  // Gérer le changement de données dans une section
  const handleSectionChange = (sectionName, sectionData) => {
    setAssessmentData(prev => ({
      ...prev,
      [sectionName]: sectionData
    }));
  };

  // Sauvegarder la section actuelle
  const handleSaveCurrentSection = async () => {
    try {
      setIsSaving(true);
      const currentSection = steps[currentStep - 1].section;
      await saveAssessmentSection(db, beneficiaryId, currentSection, assessmentData[currentSection]);
      setLastSaved(new Date());
      showToast('Section sauvegardée', 'success');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation
  const handleNext = async () => {
    await handleSaveCurrentSection();
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToStep = async (stepNumber) => {
    await handleSaveCurrentSection();
    setCurrentStep(stepNumber);
  };

  // Terminer le questionnaire
  const handleComplete = async () => {
    console.log('[Wizard] ===== handleComplete appelé =====');
    console.log('[Wizard] Current step:', currentStep);
    console.log('[Wizard] Steps length:', steps.length);
    console.log('[Wizard] Assessment data:', assessmentData);

    try {
      setIsSaving(true);
      console.log('[Wizard] isSaving défini à true');

      // Sauvegarder la section actuelle
      const currentSection = steps[currentStep - 1].section;
      console.log('[Wizard] Sauvegarde de la section:', currentSection);
      console.log('[Wizard] Données de la section à sauvegarder:', assessmentData[currentSection]);

      try {
        await saveAssessmentSection(db, beneficiaryId, currentSection, assessmentData[currentSection]);
        console.log('[Wizard] Section sauvegardée avec succès');
      } catch (saveError) {
        console.error('[Wizard] ERREUR lors de la sauvegarde de la section:', saveError);
        throw saveError;
      }

      // Marquer comme complété
      console.log('[Wizard] Marquage du questionnaire comme complété...');
      await completeAssessment(db, beneficiaryId, {
        uid: user?.uid,
        displayName: user?.displayName || user?.email,
        role: 'admin'
      });
      console.log('[Wizard] Questionnaire marqué comme complété');

      showToast('Questionnaire complété avec succès !', 'success');
      console.log('[Wizard] Toast affiché');

      // Afficher le modal d'enrichissement du profil
      console.log('[Wizard] Affichage du modal d\'enrichissement...');
      console.log('[Wizard] showEnrichmentModal avant:', showEnrichmentModal);
      setShowEnrichmentModal(true);
      console.log('[Wizard] setShowEnrichmentModal(true) appelé');
    } catch (error) {
      console.error('[Wizard] ===== ERREUR dans handleComplete =====');
      console.error('[Wizard] Erreur:', error);
      console.error('[Wizard] Stack:', error.stack);
      showToast('Erreur lors de la finalisation', 'error');
    } finally {
      setIsSaving(false);
      console.log('[Wizard] isSaving défini à false');
    }
  };

  // Gérer la confirmation d'enrichissement du profil
  const handleEnrichmentConfirm = async (enrichmentData, selectedFields) => {
    try {
      console.log('[Wizard] Enrichissement confirmé, application des données...');
      await applyProfileEnrichment(db, beneficiaryId, enrichmentData, selectedFields);
      showToast('Profil enrichi avec succès !', 'success');
      setShowEnrichmentModal(false);

      console.log('[Wizard] Enrichissement terminé, fermeture du wizard');
      if (onComplete) {
        onComplete();
      }

      onClose();
    } catch (error) {
      console.error('[Wizard] Erreur lors de l\'enrichissement:', error);
      showToast('Erreur lors de l\'enrichissement du profil', 'error');
    }
  };

  // Annuler l'enrichissement
  const handleEnrichmentCancel = () => {
    console.log('[Wizard] Enrichissement annulé, fermeture du wizard');
    setShowEnrichmentModal(false);

    if (onComplete) {
      onComplete();
    }

    onClose();
  };

  // Calculer la progression
  const completionPercentage = assessmentData ? calculateCompletionPercentage(assessmentData) : 0;

  // Rendu d'une section
  const renderSection = () => {
    if (!assessmentData) return null;

    switch (currentStep) {
      case 1:
        return (
          <AssessmentSection1_Prealables
            data={assessmentData.prealables}
            onChange={handleSectionChange}
          />
        );
      case 2:
        return (
          <AssessmentSection2_Identite
            data={{
              familialStatus: assessmentData.familialStatus,
              legalProtection: assessmentData.legalProtection
            }}
            beneficiary={beneficiary}
            onChange={handleSectionChange}
          />
        );
      case 3:
        return (
          <AssessmentSection3_Contacts
            data={assessmentData.contacts}
            onChange={handleSectionChange}
          />
        );
      case 4:
        return (
          <AssessmentSection4_Logement
            data={assessmentData.housing}
            onChange={handleSectionChange}
          />
        );
      case 5:
        return (
          <AssessmentSection5_Sante
            data={assessmentData.health}
            onChange={handleSectionChange}
          />
        );
      case 6:
        return (
          <AssessmentSection6_Autonomie
            data={assessmentData.autonomy}
            onChange={handleSectionChange}
          />
        );
      case 7:
        return (
          <AssessmentSection7_Psychologie
            data={assessmentData.psychological}
            onChange={handleSectionChange}
          />
        );
      case 8:
        return (
          <AssessmentSection8_Aides
            data={assessmentData.currentAids}
            onChange={handleSectionChange}
          />
        );
      case 9:
        return (
          <AssessmentSection9_Besoins
            data={assessmentData.needs}
            onChange={handleSectionChange}
          />
        );
      case 10:
        return (
          <AssessmentSection10_Projet
            data={assessmentData.project}
            onChange={handleSectionChange}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="assessment-wizard-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <p>Chargement du questionnaire...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-wizard-container">
      {/* Header */}
      <div className="assessment-wizard-header">
        <h2>
          <FileText size={24} />
          Questionnaire d'évaluation - {beneficiary?.firstName} {beneficiary?.lastName}
        </h2>
        <button onClick={onClose} className="assessment-wizard-close-button">
          <X size={20} />
        </button>
      </div>

      {/* Stepper */}
      <div className="assessment-stepper">
        <div className="assessment-stepper-wrapper">
          {steps.map((step) => (
            <div
              key={step.number}
              className={`assessment-step ${currentStep === step.number ? 'active' : ''} ${
                currentStep > step.number ? 'completed' : ''
              }`}
              onClick={() => handleGoToStep(step.number)}
            >
              <div className="assessment-step-number">
                {currentStep > step.number ? <CheckCircle size={20} /> : step.number}
              </div>
              <div className="assessment-step-label">{step.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress Info */}
      <div className="assessment-progress-info">
        <div className="assessment-progress-text">
          Section {currentStep} / {steps.length}
        </div>
        <div className="assessment-progress-bar">
          <div
            className="assessment-progress-fill"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <div className={`assessment-auto-save-indicator ${isSaving ? 'saving' : ''}`}>
          <Save size={14} />
          {isSaving ? 'Sauvegarde...' : lastSaved ? `Sauvegardé à ${lastSaved.toLocaleTimeString()}` : 'Auto-save actif'}
        </div>
      </div>

      {/* Body */}
      <div className="assessment-wizard-body">
        {renderSection()}
      </div>

      {/* Footer */}
      <div className="assessment-wizard-footer">
        <div className="assessment-footer-buttons">
          <div className="assessment-footer-left">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="assessment-btn assessment-btn-secondary"
            >
              <ChevronLeft size={16} />
              Précédent
            </button>
          </div>

          <div className="assessment-footer-right">
            <button
              onClick={handleSaveCurrentSection}
              disabled={isSaving}
              className="assessment-btn assessment-btn-secondary"
            >
              <Save size={16} />
              Sauvegarder
            </button>

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={isSaving}
                className="assessment-btn assessment-btn-primary"
              >
                Suivant
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={isSaving}
                className="assessment-btn assessment-btn-success"
              >
                <CheckCircle size={16} />
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'enrichissement du profil */}
      {showEnrichmentModal && (
        <>
          {console.log('[Wizard] Rendu du modal d\'enrichissement, showEnrichmentModal:', showEnrichmentModal)}
          <ProfileEnrichmentModal
            assessmentData={assessmentData}
            beneficiary={beneficiary}
            onConfirm={handleEnrichmentConfirm}
            onCancel={handleEnrichmentCancel}
          />
        </>
      )}
    </div>
  );
};

export default BeneficiaryAssessmentWizard;
